import { type CSSProperties, type ReactNode, useState } from 'react';

// ─── Shared Styles ──────────────────────────────

export const cardStyle: CSSProperties = {
  backgroundColor: '#fff',
  border: '1px solid #E8E0D8',
  borderRadius: 12,
  padding: '20px 24px',
};

export const statLabelStyle: CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.05em',
  color: '#8B7D6B',
  textTransform: 'uppercase' as const,
};

export const statValueStyle: CSSProperties = {
  fontSize: 36,
  fontWeight: 600,
  color: '#1A1714',
  lineHeight: 1.1,
  marginTop: 8,
};

export const statSubStyle: CSSProperties = {
  fontSize: 13,
  color: '#8B7D6B',
  marginTop: 6,
};

export const sectionTitleStyle: CSSProperties = {
  fontSize: 18,
  fontWeight: 600,
  color: '#1A1714',
};

export const tableHeaderStyle: CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.05em',
  color: '#8B7D6B',
  textTransform: 'uppercase' as const,
  padding: '12px 16px',
  textAlign: 'left' as const,
  borderBottom: '1px solid #E8E0D8',
};

export const tableCellStyle: CSSProperties = {
  padding: '14px 16px',
  fontSize: 14,
  color: '#1A1714',
  borderBottom: '1px solid #F0EBE4',
};

export const greenText: CSSProperties = { color: '#22883F', fontWeight: 500 };
export const redText: CSSProperties = { color: '#D44A3A', fontWeight: 500 };
export const orangeText: CSSProperties = { color: '#D46A3A', fontWeight: 500 };

// ─── Components ─────────────────────────────────

export function PageHeader({ title, subtitle, children }: { title: string; subtitle?: string; children?: ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
      <div>
        <h1 style={{ fontSize: 28, fontWeight: 700, color: '#1A1714', margin: 0 }}>{title}</h1>
        {subtitle && <p style={{ fontSize: 14, color: '#8B7D6B', margin: '4px 0 0' }}>{subtitle}</p>}
      </div>
      {children && <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>{children}</div>}
    </div>
  );
}

export function StatCard({ label, value, sub, accent, valueColor }: { label: string; value: string; sub?: string; accent?: ReactNode; valueColor?: string }) {
  return (
    <div style={{ ...cardStyle, flex: 1 }}>
      <div style={statLabelStyle}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <div style={{ ...statValueStyle, ...(valueColor ? { color: valueColor } : {}) }}>{value}</div>
        {accent}
      </div>
      {sub && <div style={statSubStyle}>{sub}</div>}
    </div>
  );
}

export function StatRow({ children }: { children: ReactNode }) {
  return <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>{children}</div>;
}

export function ToggleGroup({ options, value, onChange }: { options: string[]; value: string; onChange: (v: string) => void }) {
  return (
    <div style={{ display: 'flex', borderRadius: 8, border: '1px solid #E8E0D8', overflow: 'hidden' }}>
      {options.map((opt) => (
        <button
          key={opt}
          onClick={() => onChange(opt)}
          style={{
            padding: '6px 14px',
            fontSize: 13,
            fontWeight: 500,
            border: 'none',
            cursor: 'pointer',
            backgroundColor: value === opt ? '#1A1714' : '#fff',
            color: value === opt ? '#fff' : '#8B7D6B',
            fontFamily: '"Geist", system-ui, sans-serif',
          }}
        >
          {opt}
        </button>
      ))}
    </div>
  );
}

export function FilterPills({ options, value, onChange }: { options: Array<{ label: string; value: string; color?: string }>; value: string; onChange: (v: string) => void }) {
  return (
    <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
      {options.map((opt) => (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          style={{
            padding: '7px 16px',
            fontSize: 13,
            fontWeight: 500,
            borderRadius: 20,
            border: value === opt.value ? 'none' : '1px solid #E8E0D8',
            cursor: 'pointer',
            backgroundColor: value === opt.value ? '#1A1714' : '#fff',
            color: opt.color && value !== opt.value ? opt.color : value === opt.value ? '#fff' : '#4A4035',
            fontFamily: '"Geist", system-ui, sans-serif',
          }}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

export function Pagination({ page, totalPages, total, label, onPageChange }: { page: number; totalPages: number; total: number; label: string; onPageChange: (p: number) => void }) {
  const itemsPerPage = Math.ceil(total / totalPages) || 1;
  const start = (page - 1) * itemsPerPage + 1;
  const end = Math.min(page * itemsPerPage, total);

  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, padding: '0 4px' }}>
      <span style={{ fontSize: 13, color: '#8B7D6B' }}>Showing {start}–{end} of {total.toLocaleString()} {label}</span>
      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
        <PaginationBtn onClick={() => onPageChange(page - 1)} disabled={page <= 1}>‹</PaginationBtn>
        {[...Array(Math.min(3, totalPages))].map((_, i) => {
          const p = i + 1;
          return <PaginationBtn key={p} onClick={() => onPageChange(p)} active={page === p}>{p}</PaginationBtn>;
        })}
        {totalPages > 3 && <span style={{ padding: '0 4px', color: '#8B7D6B' }}>...</span>}
        {totalPages > 3 && <PaginationBtn onClick={() => onPageChange(totalPages)}>{totalPages}</PaginationBtn>}
        <PaginationBtn onClick={() => onPageChange(page + 1)} disabled={page >= totalPages}>›</PaginationBtn>
      </div>
    </div>
  );
}

function PaginationBtn({ children, onClick, active, disabled }: { children: ReactNode; onClick: () => void; active?: boolean; disabled?: boolean }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: '6px 12px',
        fontSize: 13,
        borderRadius: 6,
        border: active ? 'none' : '1px solid #E8E0D8',
        backgroundColor: active ? '#D46A3A' : '#fff',
        color: active ? '#fff' : disabled ? '#C5BFB8' : '#4A4035',
        cursor: disabled ? 'default' : 'pointer',
        fontWeight: active ? 600 : 400,
        fontFamily: '"Geist", system-ui, sans-serif',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {children}
    </button>
  );
}

export function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, CSSProperties> = {
    paid: { backgroundColor: '#E8F5E9', color: '#22883F', border: '1px solid #C8E6C9' },
    active: { backgroundColor: '#E8F5E9', color: '#22883F', border: '1px solid #C8E6C9' },
    completed: { backgroundColor: '#E8F5E9', color: '#22883F', border: '1px solid #C8E6C9' },
    processed: { backgroundColor: '#E8F5E9', color: '#22883F', border: '1px solid #C8E6C9' },
    failed: { backgroundColor: '#FFEBEE', color: '#D44A3A', border: '1px solid #FFCDD2' },
    error: { backgroundColor: '#FFEBEE', color: '#D44A3A', border: '1px solid #FFCDD2' },
    expired: { backgroundColor: '#FFEBEE', color: '#D44A3A', border: '1px solid #FFCDD2' },
    pending: { backgroundColor: '#FFF3E0', color: '#D46A3A', border: '1px solid #FFE0B2' },
    past_due: { backgroundColor: '#FFEBEE', color: '#D44A3A', border: '1px solid #FFCDD2' },
    trialing: { backgroundColor: '#E3F2FD', color: '#1565C0', border: '1px solid #BBDEFB' },
  };
  const safeStatus = status || 'unknown';
  const s = styles[safeStatus.toLowerCase()] ?? { backgroundColor: '#F5F2EC', color: '#8B7D6B', border: '1px solid #E8E0D8' };
  return (
    <span style={{ ...s, padding: '3px 10px', borderRadius: 4, fontSize: 12, fontWeight: 600, textTransform: 'capitalize', whiteSpace: 'nowrap' }}>
      {safeStatus.replace('_', ' ')}
    </span>
  );
}

export function Avatar({ name, size = 36 }: { name?: string | null; size?: number }) {
  const safeName = name || '?';
  const initials = safeName.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const colors = ['#D46A3A', '#8B7355', '#5B8A72', '#6B5B95', '#D4883A', '#4A6FA5'];
  const idx = safeName.split('').reduce((a, c) => a + c.charCodeAt(0), 0) % colors.length;
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', backgroundColor: colors[idx],
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: '#fff', fontSize: size * 0.38, fontWeight: 600, flexShrink: 0,
    }}>
      {initials}
    </div>
  );
}

export function PlanBadge({ plan, pastDue }: { plan: string; pastDue?: boolean }) {
  if (pastDue) {
    return (
      <span style={{ backgroundColor: '#FDE8E8', color: '#D44A3A', border: '1px solid #F0A8A8', padding: '3px 10px', borderRadius: 4, fontSize: 11, fontWeight: 700, letterSpacing: '0.03em' }}>
        PAST DUE
      </span>
    );
  }
  const styles: Record<string, CSSProperties> = {
    free: { backgroundColor: '#F0E8DF', color: '#8B5E3C', border: '1px solid #D9C4AE' },
    pro: { backgroundColor: '#FFF0E8', color: '#D46A3A', border: '1px solid #F0C4A8' },
    enterprise: { backgroundColor: '#F0E6FF', color: '#7C3AED', border: '1px solid #C4A8F0' },
  };
  const labels: Record<string, string> = { free: 'FREE', pro: 'PRO', enterprise: 'ENT' };
  const s = styles[plan] ?? styles.free;
  return (
    <span style={{ ...s, padding: '3px 10px', borderRadius: 4, fontSize: 11, fontWeight: 700, letterSpacing: '0.03em' }}>
      {labels[plan] ?? (plan || 'FREE').toUpperCase()}
    </span>
  );
}

export function useAdminData<T>(fetcher: () => Promise<T>, _deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = () => {
    setLoading(true);
    setError(null);
    fetcher()
      .then(setData)
      .catch((err) => setError(err?.response?.data?.message || err.message || 'Failed to load'))
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useState(() => { reload(); });

  return { data, loading, error, reload };
}

export function LoadingState() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 60, color: '#8B7D6B', fontSize: 14 }}>
      Loading...
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 60, gap: 12 }}>
      <span style={{ color: '#D44A3A', fontSize: 14 }}>{message}</span>
      {onRetry && (
        <button onClick={onRetry} style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid #E8E0D8', backgroundColor: '#fff', cursor: 'pointer', fontSize: 13, fontFamily: '"Geist", system-ui, sans-serif' }}>
          Retry
        </button>
      )}
    </div>
  );
}

export function timeAgo(date: string | Date | null | undefined): string {
  if (!date) return '—';
  const now = Date.now();
  const then = new Date(date).getTime();
  if (isNaN(then)) return '—';
  const diff = now - then;
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} days ago`;
  return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function formatCurrency(cents: number | null | undefined): string {
  if (cents == null) return '—';
  return `$${(cents / 100).toFixed(2)}`;
}

export function formatDate(date: string | Date | null | undefined): string {
  if (!date) return '—';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
